import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Badge, Spinner, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FileText, Check, X, User, BookOpen, Clock, AlertCircle, Eye, MessageSquare, MapPin, Send, MessageCircle, ZoomIn, ZoomOut, ArrowLeft, Maximize2, Minimize2, PenLine, Printer } from 'lucide-react';
import api from '../api';
import CustomCertPrintModal from './CustomCertPrintModal';

const CertificateRequestManager = ({ onClose, activeRole }) => {
  const [activeTab, setActiveTab] = useState('regular');

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  // panel: null = lista principal | 'approve' | 'reject' | 'comment'
  const [panel, setPanel] = useState(null);
  const [modalType, setModalType] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [commentText, setCommentText] = useState('');
  const [zoom, setZoom] = useState(0.65);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Constancias personalizadas
  const [customRequests, setCustomRequests] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(true);
  const [processingCustomId, setProcessingCustomId] = useState(null);
  const [selectedCustom, setSelectedCustom] = useState(null);
  const [customPanel, setCustomPanel] = useState(null); // null | 'elevate' | 'reject' | 'issue'
  const [cdComment, setCdComment] = useState('');
  const [cdCommentVisibility, setCdCommentVisibility] = useState('ACO');
  const [customRejectionReason, setCustomRejectionReason] = useState('');
  const [issueStep, setIssueStep] = useState('form'); // 'form' | 'preview'
  const [customTitle, setCustomTitle] = useState('CONSTANCIA');
  const [customText, setCustomText] = useState('');
  const [viewTarget, setViewTarget] = useState(null);

  useEffect(() => { fetchRequests(true); fetchCustomRequests(); }, []);

  const fetchRequests = async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);
      const res = await api.get('certificate-requests/');
      setRequests(res.data);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  const goBack = () => {
    setPanel(null);
    setSelectedRequest(null);
    setRejectionReason('');
    setCommentText('');
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const fetchCustomRequests = async () => {
    try {
      setLoadingCustom(true);
      const res = await api.get('custom-certificate-requests/');
      setCustomRequests(res.data);
    } catch (err) {
      console.error('Error fetching custom requests:', err);
    } finally {
      setLoadingCustom(false);
    }
  };

  const goBackCustom = () => {
    setCustomPanel(null);
    setSelectedCustom(null);
    setCdComment('');
    setCdCommentVisibility('ACO');
    setCustomRejectionReason('');
    setIssueStep('form');
    setCustomTitle('CONSTANCIA');
    setCustomText('');
  };

  const openElevateCustomPanel = (req) => {
    setSelectedCustom(req);
    setCdComment('');
    setCdCommentVisibility('ACO');
    setCustomPanel('elevate');
  };

  const openRejectCustomPanel = (req) => {
    setSelectedCustom(req);
    setCustomRejectionReason('');
    setCustomPanel('reject');
  };

  const openIssuePanel = (req) => {
    setSelectedCustom(req);
    setCustomTitle('CONSTANCIA');
    setCustomText('');
    setIssueStep('form');
    setCustomPanel('issue');
  };

  const handleElevateCustom = async () => {
    if (!selectedCustom || processingCustomId) return;
    setProcessingCustomId(selectedCustom.id);
    try {
      await api.post(`custom-certificate-requests/${selectedCustom.id}/elevate/`, {
        comment: cdComment,
        visibility: cdCommentVisibility,
      });
      goBackCustom();
      fetchCustomRequests();
    } catch (err) {
      console.error('Error elevating custom request:', err);
      alert('Error al elevar la solicitud.');
    } finally {
      setProcessingCustomId(null);
    }
  };

  const handleRejectCustom = async () => {
    if (!selectedCustom || !customRejectionReason || processingCustomId) {
      if (!customRejectionReason) alert('Ingresá un motivo de rechazo.');
      return;
    }
    setProcessingCustomId(selectedCustom.id);
    try {
      await api.post(`custom-certificate-requests/${selectedCustom.id}/reject/`, { reason: customRejectionReason });
      goBackCustom();
      fetchCustomRequests();
    } catch (err) {
      console.error('Error rejecting custom request:', err);
      alert('Error al rechazar la solicitud.');
    } finally {
      setProcessingCustomId(null);
    }
  };

  const handleIssueCustom = async () => {
    if (!selectedCustom || !customText.trim() || processingCustomId) {
      if (!customText.trim()) alert('El texto de la constancia no puede estar vacío.');
      return;
    }
    setProcessingCustomId(selectedCustom.id);
    try {
      await api.post(`custom-certificate-requests/${selectedCustom.id}/issue/`, {
        aco_custom_text: customText,
        certificate_title: customTitle,
      });
      goBackCustom();
      fetchCustomRequests();
    } catch (err) {
      console.error('Error issuing custom certificate:', err);
      alert('Error al emitir la constancia.');
    } finally {
      setProcessingCustomId(null);
    }
  };

  const openApprovePanel = async (req) => {
    setSelectedRequest(req);
    setZoom(0.65);
    setPreviewUrl(null);
    setPanel('approve');
    setPreviewLoading(true);
    try {
      const res = await api.get(`certificate-requests/${req.id}/preview/`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Error cargando preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openRejectPanel = (req) => {
    setSelectedRequest(req);
    setRejectionReason('');
    setPanel('reject');
  };

  const openCommentPanel = (req, type) => {
    setSelectedRequest(req);
    setModalType(type);
    setCommentText(type === 'cd_comment' ? (req.cd_comment || '') : type === 'rm_comment' ? (req.rm_comment || '') : '');
    setPanel('comment');
  };

  const handleConfirmApprove = async () => {
    if (!selectedRequest || processingId) return;
    const req = selectedRequest;
    setProcessingId(req.id);
    try {
      await api.post(`certificate-requests/${req.id}/approve/`);
      goBack();
      fetchRequests();
    } catch (err) {
      console.error('Error approving:', err);
      alert(err.response?.data?.error || 'Error al aprobar la constancia.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason || processingId) {
      if (!rejectionReason) alert('Por favor, ingrese un motivo de rechazo.');
      return;
    }
    const req = selectedRequest;
    setProcessingId(req.id);
    try {
      await api.post(`certificate-requests/${req.id}/reject/`, { reason: rejectionReason });
      goBack();
      fetchRequests();
    } catch (err) {
      console.error('Error rejecting:', err);
      alert(err.response?.data?.error || 'Error al rechazar la constancia.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleElevate = async () => {
    if (!selectedRequest || processingId) return;
    const req = selectedRequest;
    setProcessingId(req.id);
    try {
      await api.post(`certificate-requests/${req.id}/elevate/`, { comment: commentText });
      goBack();
      fetchRequests();
    } catch (err) {
      console.error('Error elevating:', err);
      alert(err.response?.data?.error || 'Error al elevar la solicitud.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCommentCD = async () => {
    if (!selectedRequest || processingId) return;
    const req = selectedRequest;
    setProcessingId(req.id);
    try {
      await api.post(`certificate-requests/${req.id}/comment_cd/`, { comment: commentText });
      goBack();
      fetchRequests();
    } catch (err) {
      console.error('Error CD comment:', err);
      alert(err.response?.data?.error || 'Error al guardar el comentario.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCommentRM = async () => {
    if (!selectedRequest || processingId) return;
    const req = selectedRequest;
    setProcessingId(req.id);
    try {
      await api.post(`certificate-requests/${req.id}/comment_rm/`, { comment: commentText });
      goBack();
      fetchRequests();
    } catch (err) {
      console.error('Error RM comment:', err);
      alert(err.response?.data?.error || 'Error al guardar el comentario.');
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PE': return <Badge bg="warning" className="px-2 py-1"><Clock size={12} className="me-1"/>Pendiente</Badge>;
      case 'VC': return <Badge bg="info" className="px-2 py-1"><Check size={12} className="me-1"/>Avalada por CD</Badge>;
      case 'AP': return <Badge bg="success" className="px-2 py-1"><Check size={12} className="me-1"/>Aprobada</Badge>;
      case 'RE': return <Badge bg="danger" className="px-2 py-1"><X size={12} className="me-1"/>Rechazada</Badge>;
      default:   return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getCustomStatusBadge = (status) => {
    switch (status) {
      case 'PE': return <Badge bg="warning" className="px-2 py-1"><Clock size={12} className="me-1"/>Pendiente (CD)</Badge>;
      case 'VC': return <Badge bg="info"    className="px-2 py-1"><Send size={12} className="me-1"/>Elevada al ACO</Badge>;
      case 'EM': return <Badge bg="success" className="px-2 py-1"><Check size={12} className="me-1"/>Emitida</Badge>;
      case 'RE': return <Badge bg="danger"  className="px-2 py-1"><X size={12} className="me-1"/>Rechazada</Badge>;
      default:   return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const isStaff = ['ACO', 'CO', 'CG'].includes(activeRole);
  const isCD    = activeRole === 'CD';
  const isRM    = activeRole === 'RM';

  const pendingRegular = requests.filter(r => r.status === 'PE').length;
  const pendingCustom  = isCD
    ? customRequests.filter(r => r.status === 'PE').length
    : customRequests.filter(r => r.status === 'VC').length;

  // ─── Panel: Aprobar con previsualización ───
  const renderApprovePanel = () => (
    <div className="d-flex flex-column h-100">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={goBack} disabled={!!processingId}>
          <ArrowLeft size={15} className="me-1" /> Volver
        </Button>
        <span className="fw-bold text-primary">Previsualización — Constancia de Alumno Regular</span>
      </div>

      {/* Resumen del alumno */}
      <div className="bg-white rounded-3 p-3 mb-3 shadow-sm d-flex flex-column gap-1 flex-shrink-0">
        <div className="d-flex align-items-center gap-2">
          <User size={14} className="text-muted" /><span className="fw-bold">{selectedRequest?.student_name}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <BookOpen size={14} className="text-primary" /><span className="small">{selectedRequest?.diploma_name}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <MapPin size={14} className="text-muted" /><span className="small">{selectedRequest?.municipality}</span>
        </div>
        {selectedRequest?.rm_comment && (
          <div className="d-flex align-items-start gap-2 text-info small">
            <MessageCircle size={13} className="flex-shrink-0 mt-1" />
            <span><strong>Aval RM:</strong> {selectedRequest.rm_comment}</span>
          </div>
        )}
        {selectedRequest?.cd_comment && (
          <div className="d-flex align-items-start gap-2 text-primary small">
            <Check size={13} className="flex-shrink-0 mt-1" />
            <span><strong>VºBº CD:</strong> {selectedRequest.cd_comment}</span>
          </div>
        )}
      </div>

      {/* Vista desktop: iframe con zoom */}
      <div className="d-none d-md-flex flex-column flex-grow-1 bg-dark bg-opacity-75 rounded-3 overflow-auto mb-3" style={{ minHeight: 0 }}>
        <div className="d-flex align-items-center gap-1 p-2 bg-white border-bottom flex-shrink-0">
          <Button variant="link" className="p-0 text-muted" onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}><ZoomOut size={16}/></Button>
          <span className="small fw-bold text-muted mx-1" style={{ minWidth: '40px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <Button variant="link" className="p-0 text-muted" onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}><ZoomIn size={16}/></Button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '16px' }}>
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
            {previewLoading
              ? <div style={{ width: '794px', height: '400px', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#666' }}><Spinner animation="border" size="sm" /> Cargando vista previa...</div>
              : previewUrl
                ? <iframe src={previewUrl} title="Vista previa" style={{ width: '794px', height: '1123px', border: 'none', display: 'block' }} />
                : <div style={{ width: '794px', height: '200px', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c00' }}>Error al cargar el PDF.</div>
            }
          </div>
        </div>
      </div>

      {/* Vista mobile: botón abrir PDF */}
      <div className="d-flex d-md-none flex-column gap-2 mb-3">
        {previewLoading
          ? <div className="text-center text-muted small py-2"><Spinner animation="border" size="sm" className="me-2"/>Generando PDF...</div>
          : previewUrl
            ? <a href={previewUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-2">
                <Eye size={16}/> Ver PDF en nueva pestaña
              </a>
            : null
        }
        <p className="text-muted small text-center mb-0">Al confirmar, el alumno podrá descargar su constancia.</p>
      </div>

      <Button
        variant="success" size="lg"
        onClick={handleConfirmApprove}
        disabled={!!processingId}
        className="d-flex align-items-center justify-content-center gap-2 fw-bold shadow-sm flex-shrink-0"
      >
        {processingId ? <Spinner animation="border" size="sm"/> : <Check size={20}/>}
        Confirmar Aprobación
      </Button>
    </div>
  );

  // ─── Panel: Rechazar ───
  const renderRejectPanel = () => (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex align-items-center gap-2">
        <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={goBack} disabled={!!processingId}>
          <ArrowLeft size={15} className="me-1"/> Volver
        </Button>
        <span className="fw-bold text-danger d-flex align-items-center gap-1"><AlertCircle size={18}/> Rechazar Solicitud</span>
      </div>
      <div className="bg-white rounded-3 p-3 shadow-sm d-flex flex-column gap-1">
        <div className="d-flex align-items-center gap-2"><User size={14} className="text-muted"/><span className="fw-bold">{selectedRequest?.student_name}</span></div>
        <div className="d-flex align-items-center gap-2"><BookOpen size={14} className="text-primary"/><span className="small">{selectedRequest?.diploma_name}</span></div>
      </div>
      <p className="small text-muted mb-0">
        Indicá el motivo del rechazo. Este mensaje le llegará al alumno.
      </p>
      <Form.Group>
        <Form.Label className="fw-bold small text-muted text-uppercase">Motivo de Rechazo</Form.Label>
        <Form.Control
          as="textarea" rows={3}
          placeholder="Ej: El alumno no cumple con la asistencia mínima requerida..."
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          className="bg-light border-0 shadow-sm rounded-3"
        />
      </Form.Group>
      <div className="d-flex gap-2">
        <Button variant="light" onClick={goBack} disabled={!!processingId} className="rounded-pill px-4">Cancelar</Button>
        <Button
          variant="danger" onClick={handleReject}
          disabled={!rejectionReason || !!processingId}
          className="rounded-pill px-4 fw-bold shadow-sm flex-grow-1"
        >
          {processingId ? <Spinner animation="border" size="sm"/> : 'Confirmar Rechazo'}
        </Button>
      </div>
    </div>
  );

  // ─── Panel: Comentar / Elevar / Aval RM ───
  const renderCommentPanel = () => {
    const isElevate = modalType === 'elevate';
    const isCDComment = modalType === 'cd_comment';
    const title = isElevate ? 'Elevar al Auxiliar Operativo' : isCDComment ? 'Comentario del Coordinador' : 'Aval Municipal';
    const placeholder = isElevate
      ? 'Ej: Avalo la solicitud, el/la alumno/a ha demostrado regularidad...'
      : isCDComment ? 'Ej: Pendiente de verificar asistencia...'
      : 'Soy de opinión que se le dé curso a la solicitud...';
    const label = isElevate ? 'Observación (Opcional)' : isCDComment ? 'Comentario (Opcional)' : 'Aval / Observación (Opcional)';
    const color = isElevate ? 'text-primary' : isCDComment ? 'text-secondary' : 'text-info';
    const Icon = isElevate ? Send : MessageSquare;
    const onConfirm = isElevate ? handleElevate : isCDComment ? handleCommentCD : handleCommentRM;
    const confirmLabel = isElevate ? 'Elevar Solicitud' : isCDComment ? 'Guardar Comentario' : 'Guardar Aval';
    const confirmVariant = isElevate ? 'primary' : isCDComment ? 'secondary' : 'info';

    return (
      <div className="d-flex flex-column gap-3">
        <div className="d-flex align-items-center gap-2">
          <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={goBack} disabled={!!processingId}>
            <ArrowLeft size={15} className="me-1"/> Volver
          </Button>
          <span className={`fw-bold d-flex align-items-center gap-1 ${color}`}><Icon size={18}/> {title}</span>
        </div>
        <div className="bg-white rounded-3 p-3 shadow-sm d-flex flex-column gap-1">
          <div className="d-flex align-items-center gap-2"><User size={14} className="text-muted"/><span className="fw-bold">{selectedRequest?.student_name}</span></div>
          <div className="d-flex align-items-center gap-2"><BookOpen size={14} className="text-primary"/><span className="small">{selectedRequest?.diploma_name}</span></div>
        </div>
        <p className="small text-muted mb-0">
          {isElevate
            ? 'Está dando el visto bueno y elevando esta solicitud para su aprobación final.'
            : isCDComment
            ? 'Puede dejar una observación interna sin modificar el estado de la solicitud.'
            : 'Como referente municipal, puede dejar una nota de aval sobre el alumno.'}
        </p>
        <Form.Group>
          <Form.Label className="fw-bold small text-muted text-uppercase">{label}</Form.Label>
          <Form.Control
            as="textarea" rows={4}
            placeholder={placeholder}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className="bg-light border-0 shadow-sm rounded-3"
          />
        </Form.Group>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={goBack} disabled={!!processingId} className="rounded-pill px-4">Cancelar</Button>
          <Button
            variant={confirmVariant} onClick={onConfirm}
            disabled={!!processingId}
            className="rounded-pill px-4 fw-bold shadow-sm flex-grow-1 text-white"
          >
            {processingId ? <Spinner animation="border" size="sm"/> : confirmLabel}
          </Button>
        </div>
      </div>
    );
  };

  // ─── Personalizadas: elevar (CD) ───
  const renderElevateCustomPanel = () => (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex align-items-center gap-2">
        <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={goBackCustom} disabled={!!processingCustomId}>
          <ArrowLeft size={15} className="me-1"/> Volver
        </Button>
        <span className="fw-bold text-primary d-flex align-items-center gap-1"><Send size={18}/> Elevar Constancia Personalizada</span>
      </div>
      {selectedCustom && (
        <div className="bg-white rounded-3 p-3 shadow-sm">
          <div className="small fw-bold text-info mb-1">Descripción del alumno ({selectedCustom.student_name}):</div>
          <div className="small text-dark">{selectedCustom.student_description}</div>
        </div>
      )}
      <Form.Group>
        <Form.Label className="fw-bold small text-muted text-uppercase">Comentario (opcional)</Form.Label>
        <Form.Control as="textarea" rows={3}
          placeholder="Indicaciones para el ACO, aclaraciones sobre el pedido..."
          value={cdComment} onChange={(e) => setCdComment(e.target.value)}
          className="bg-light border-0 shadow-sm rounded-3" />
      </Form.Group>
      {cdComment.trim() && (
        <Form.Group>
          <Form.Label className="fw-bold small text-muted text-uppercase">¿Quién puede ver este comentario?</Form.Label>
          <div className="d-flex flex-column gap-2 mt-1">
            <Form.Check type="radio" id="vis-aco"
              label={<span><strong>Solo el ACO</strong><span className="text-muted ms-2 small">— el alumno no lo verá</span></span>}
              checked={cdCommentVisibility === 'ACO'} onChange={() => setCdCommentVisibility('ACO')} />
            <Form.Check type="radio" id="vis-al"
              label={<span><strong>El alumno y el ACO</strong><span className="text-muted ms-2 small">— se notifica al alumno por mensaje</span></span>}
              checked={cdCommentVisibility === 'AL'} onChange={() => setCdCommentVisibility('AL')} />
          </div>
        </Form.Group>
      )}
      <div className="d-flex gap-2">
        <Button variant="light" onClick={goBackCustom} disabled={!!processingCustomId} className="rounded-pill px-4">Cancelar</Button>
        <Button variant="primary" onClick={handleElevateCustom} disabled={!!processingCustomId}
          className="rounded-pill px-4 fw-bold shadow-sm flex-grow-1">
          {processingCustomId ? <Spinner animation="border" size="sm"/> : <><Send size={14} className="me-1"/>Elevar al ACO</>}
        </Button>
      </div>
    </div>
  );

  // ─── Personalizadas: rechazar (CD o ACO) ───
  const renderRejectCustomPanel = () => (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex align-items-center gap-2">
        <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={goBackCustom} disabled={!!processingCustomId}>
          <ArrowLeft size={15} className="me-1"/> Volver
        </Button>
        <span className="fw-bold text-danger d-flex align-items-center gap-1"><AlertCircle size={18}/> Rechazar Solicitud Personalizada</span>
      </div>
      <p className="small text-muted mb-0">
        Alumno: <strong>{selectedCustom?.student_name}</strong><br />
        Descripción: <em>{selectedCustom?.student_description}</em>
      </p>
      <Form.Group>
        <Form.Label className="fw-bold small text-muted text-uppercase">Motivo de Rechazo</Form.Label>
        <Form.Control as="textarea" rows={3} placeholder="Se notificará al alumno..."
          value={customRejectionReason} onChange={(e) => setCustomRejectionReason(e.target.value)}
          className="bg-light border-0 shadow-sm rounded-3" />
      </Form.Group>
      <div className="d-flex gap-2">
        <Button variant="light" onClick={goBackCustom} disabled={!!processingCustomId} className="rounded-pill px-4">Cancelar</Button>
        <Button variant="danger" onClick={handleRejectCustom} disabled={!customRejectionReason || !!processingCustomId}
          className="rounded-pill px-4 fw-bold shadow-sm flex-grow-1">
          {processingCustomId ? <Spinner animation="border" size="sm"/> : 'Confirmar Rechazo'}
        </Button>
      </div>
    </div>
  );

  // ─── Personalizadas: redactar (ACO) — paso 1: formulario ───
  const renderIssueCustomForm = () => (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex align-items-center gap-2">
        <Button variant="outline-secondary" size="sm" className="rounded-pill" onClick={goBackCustom} disabled={!!processingCustomId}>
          <ArrowLeft size={15} className="me-1"/> Volver
        </Button>
        <span className="fw-bold text-primary d-flex align-items-center gap-1"><PenLine size={18}/> Redactar Constancia Personalizada</span>
      </div>
      {selectedCustom && (
        <>
          <div className="bg-white rounded-3 p-3 shadow-sm">
            <div className="small fw-bold text-info mb-1">Descripción del alumno ({selectedCustom.student_name}):</div>
            <div className="small text-dark">{selectedCustom.student_description}</div>
          </div>
          {selectedCustom.cd_comment && (
            <div className="p-3 bg-warning bg-opacity-10 rounded-3 border-start border-3 border-warning">
              <div className="small fw-bold text-warning-emphasis mb-1">Comentario del coordinador:</div>
              <div className="small text-dark">{selectedCustom.cd_comment}</div>
            </div>
          )}
        </>
      )}
      <Form.Group>
        <Form.Label className="fw-bold small text-muted text-uppercase">Título del certificado</Form.Label>
        <Form.Control type="text" value={customTitle}
          onChange={(e) => setCustomTitle(e.target.value.toUpperCase())}
          placeholder="Ej: CONSTANCIA DE PARTICIPACIÓN"
          className="bg-light border-0 shadow-sm rounded-3" />
        <Form.Text className="text-muted">Aparecerá centrado y subrayado en el documento.</Form.Text>
      </Form.Group>
      <Form.Group>
        <Form.Label className="fw-bold small text-muted text-uppercase">
          Texto del certificado <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control as="textarea" rows={6} value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          placeholder="Redactá el cuerpo de la constancia..."
          className="bg-light border-0 shadow-sm rounded-3" />
        <Form.Text className="text-muted">El nombre y DNI del alumno ya aparecen en el encabezado.</Form.Text>
      </Form.Group>
      <div className="d-flex gap-2">
        <Button variant="light" onClick={goBackCustom} disabled={!!processingCustomId} className="rounded-pill px-4">Cancelar</Button>
        <Button variant="primary" onClick={() => setIssueStep('preview')} disabled={!customText.trim()}
          className="rounded-pill px-4 fw-bold shadow-sm flex-grow-1 d-flex align-items-center justify-content-center gap-2">
          <Printer size={16}/> Ver borrador →
        </Button>
      </div>
    </div>
  );

  // ─── Personalizadas: lista ───
  const renderCustomList = () => (
    <>
      {/* Vista desktop: tabla */}
      <div className="d-none d-md-block table-responsive shadow-sm rounded-4 bg-white overflow-hidden">
        <Table hover className="align-middle mb-0 premium-table">
          <thead className="table-light text-muted small text-uppercase">
            <tr>
              <th className="ps-4">Alumno</th>
              <th>Diplomatura</th>
              <th>Descripción del alumno</th>
              <th>Comentario CD</th>
              <th className="text-center">Estado</th>
              <th className="pe-4 text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {customRequests.map(req => (
              <tr key={req.id} className="border-bottom-light">
                <td className="ps-4">
                  <div className="d-flex align-items-center gap-2">
                    <div className="p-2 bg-light rounded-circle"><User size={16}/></div>
                    <div>
                      <div className="fw-bold text-dark">{req.student_name}</div>
                      <div className="x-small text-muted">{req.student_email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <BookOpen size={14} className="text-primary"/>
                    <span className="small fw-medium">{req.diploma_name}</span>
                  </div>
                </td>
                <td style={{ maxWidth: '220px' }}>
                  <div className="small text-dark p-2 bg-info bg-opacity-10 rounded border-start border-3 border-info">
                    {req.student_description}
                  </div>
                </td>
                <td style={{ maxWidth: '200px' }}>
                  {req.cd_comment ? (
                    <div className="small p-2 bg-warning bg-opacity-10 rounded border-start border-3 border-warning">
                      <div className="fw-semibold text-warning-emphasis mb-1" style={{ fontSize: '0.7rem' }}>
                        {req.cd_comment_visibility === 'AL' ? 'Visible para alumno y ACO' : 'Solo para el ACO'}
                      </div>
                      {req.cd_comment}
                    </div>
                  ) : (
                    <span className="text-muted x-small fst-italic">Sin comentario</span>
                  )}
                </td>
                <td className="text-center">{getCustomStatusBadge(req.status)}</td>
                <td className="pe-4 text-end">
                  <div className="d-flex justify-content-end gap-2 flex-wrap">
                    {isCD && req.status === 'PE' && (
                      <>
                        <Button variant="outline-danger" size="sm" className="rounded-pill px-3"
                          onClick={() => openRejectCustomPanel(req)} disabled={processingCustomId === req.id}>
                          <X size={14}/> Rechazar
                        </Button>
                        <Button variant="primary" size="sm" className="rounded-pill px-3 fw-bold d-flex align-items-center gap-1 shadow-sm"
                          onClick={() => openElevateCustomPanel(req)} disabled={processingCustomId === req.id}>
                          <Send size={14}/> Elevar al ACO
                        </Button>
                      </>
                    )}
                    {isCD && req.status !== 'PE' && (
                      <span className="small text-muted fst-italic">
                        {req.status === 'VC' ? 'En proceso por ACO' : req.status === 'EM' ? 'Emitida' : 'Rechazada'}
                      </span>
                    )}
                    {isStaff && req.status === 'VC' && (
                      <>
                        <Button variant="outline-danger" size="sm" className="rounded-pill px-3"
                          onClick={() => openRejectCustomPanel(req)} disabled={processingCustomId === req.id}>
                          <X size={14}/> Rechazar
                        </Button>
                        <Button variant="primary" size="sm" className="rounded-pill px-3 fw-bold d-flex align-items-center gap-1 shadow-sm"
                          onClick={() => openIssuePanel(req)} disabled={processingCustomId === req.id}>
                          <PenLine size={14}/> Redactar y Emitir
                        </Button>
                      </>
                    )}
                    {isStaff && req.status === 'PE' && (
                      <span className="small text-muted fst-italic">Esperando VºBº del coordinador</span>
                    )}
                    {req.status === 'EM' && (
                      <Button variant="outline-success" size="sm" className="rounded-pill px-3 d-flex align-items-center gap-1"
                        onClick={() => setViewTarget(req)}>
                        <Printer size={14}/> Ver / Imprimir
                      </Button>
                    )}
                    {req.status === 'RE' && (
                      <div className="text-muted x-small fst-italic"><AlertCircle size={12} className="me-1"/>{req.rejection_reason}</div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Vista mobile: tarjetas verticales */}
      <div className="d-flex d-md-none flex-column gap-3">
        {customRequests.map(req => (
          <div key={req.id} className="bg-white rounded-3 shadow-sm overflow-hidden">
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <div className="p-2 bg-light rounded-circle"><User size={15}/></div>
                <div>
                  <div className="fw-bold text-dark" style={{fontSize:'0.9rem'}}>{req.student_name}</div>
                  <div className="text-muted" style={{fontSize:'0.72rem'}}>{req.student_email}</div>
                </div>
              </div>
              {getCustomStatusBadge(req.status)}
            </div>
            <div className="px-3 py-2 d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-2">
                <BookOpen size={13} className="text-primary flex-shrink-0"/>
                <span style={{fontSize:'0.85rem'}}>{req.diploma_name}</span>
              </div>
              <div className="small text-dark p-2 bg-info bg-opacity-10 rounded border-start border-3 border-info" style={{fontSize:'0.8rem'}}>
                {req.student_description}
              </div>
              {req.cd_comment && (
                <div className="small p-2 bg-warning bg-opacity-10 rounded border-start border-3 border-warning" style={{fontSize:'0.78rem'}}>
                  {req.cd_comment}
                </div>
              )}
              {req.status === 'RE' && (
                <div className="d-flex align-items-start gap-2 text-danger" style={{fontSize:'0.78rem'}}>
                  <AlertCircle size={12} className="flex-shrink-0 mt-1"/><span>{req.rejection_reason}</span>
                </div>
              )}
            </div>
            <div className="px-3 pb-3 d-flex flex-wrap gap-2 border-top pt-2">
              {isCD && req.status === 'PE' && (
                <>
                  <Button variant="outline-danger" size="sm" className="rounded-pill" onClick={() => openRejectCustomPanel(req)}>
                    <X size={13} className="me-1"/>Rechazar
                  </Button>
                  <Button variant="primary" size="sm" className="rounded-pill fw-bold" onClick={() => openElevateCustomPanel(req)}>
                    <Send size={13} className="me-1"/>Elevar
                  </Button>
                </>
              )}
              {isStaff && req.status === 'VC' && (
                <>
                  <Button variant="outline-danger" size="sm" className="rounded-pill" onClick={() => openRejectCustomPanel(req)}>
                    <X size={13} className="me-1"/>Rechazar
                  </Button>
                  <Button variant="primary" size="sm" className="rounded-pill fw-bold" onClick={() => openIssuePanel(req)}>
                    <PenLine size={13} className="me-1"/>Redactar
                  </Button>
                </>
              )}
              {req.status === 'EM' && (
                <Button variant="outline-success" size="sm" className="rounded-pill" onClick={() => setViewTarget(req)}>
                  <Printer size={13} className="me-1"/>Ver / Imprimir
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  // ─── Lista principal ───
  const renderList = () => (
    <>
      {/* Vista desktop: tabla */}
      <div className="d-none d-md-block table-responsive shadow-sm rounded-4 bg-white overflow-hidden">
        <Table hover className="align-middle mb-0 premium-table">
          <thead className="table-light text-muted small text-uppercase">
            <tr>
              <th className="ps-4">Alumno</th>
              <th>Diplomatura</th>
              <th>Municipio / Aval RM</th>
              <th className="text-center">VºBº CD</th>
              <th className="text-center">Estado</th>
              <th className="pe-4 text-end">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {requests.map(req => (
              <tr key={req.id} className="border-bottom-light">
                <td className="ps-4">
                  <div className="d-flex align-items-center gap-2">
                    <div className="p-2 bg-light rounded-circle"><User size={16}/></div>
                    <div>
                      <div className="fw-bold text-dark">{req.student_name}</div>
                      <div className="x-small text-muted">{req.student_email}</div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <BookOpen size={14} className="text-primary"/>
                    <span className="small fw-medium">{req.diploma_name}</span>
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column gap-1">
                    <div className="d-flex align-items-center gap-2">
                      <MapPin size={14} className="text-muted"/>
                      <span className="small">{req.municipality}</span>
                    </div>
                    {req.rm_comment && (
                      <div className="x-small text-info">
                        <MessageCircle size={10} className="me-1"/>
                        <em>RM: {req.rm_comment}</em>
                      </div>
                    )}
                  </div>
                </td>
                <td className="text-center">
                  {req.processed_by_cd ? (
                    <OverlayTrigger overlay={<Tooltip>{req.cd_comment || 'Sin comentario'}</Tooltip>}>
                      <div className="text-success small fw-bold" style={{ cursor: 'help' }}>
                        <Check size={14}/> {req.processed_by_cd_name || 'OK'}
                      </div>
                    </OverlayTrigger>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </td>
                <td className="text-center">{getStatusBadge(req.status)}</td>
                <td className="pe-4 text-end">
                  <div className="d-flex justify-content-end gap-2 flex-wrap">
                    {isCD && !['AP', 'RE'].includes(req.status) && (
                      <Button variant="outline-secondary" size="sm" className="rounded-pill px-3"
                        onClick={() => openCommentPanel(req, 'cd_comment')}
                        disabled={processingId === req.id}>
                        <MessageSquare size={14}/> Comentar
                      </Button>
                    )}
                    {isCD && ['PE', 'VC'].includes(req.status) && (
                      <Button variant="outline-danger" size="sm" className="rounded-pill px-3"
                        onClick={() => openRejectPanel(req)}
                        disabled={processingId === req.id}>
                        <X size={14}/> Rechazar
                      </Button>
                    )}
                    {isCD && req.status === 'PE' && (
                      <Button variant="primary" size="sm" className="rounded-pill px-3 fw-bold d-flex align-items-center gap-1 shadow-sm"
                        onClick={() => openCommentPanel(req, 'elevate')}
                        disabled={processingId === req.id}>
                        <Send size={14}/> Elevar
                      </Button>
                    )}
                    {isRM && !['AP', 'RE'].includes(req.status) && (
                      <Button variant="outline-info" size="sm" className="rounded-pill px-3"
                        onClick={() => openCommentPanel(req, 'rm_comment')}
                        disabled={processingId === req.id}>
                        <MessageSquare size={14}/> {req.rm_comment ? 'Editar Aval' : 'Dar Aval'}
                      </Button>
                    )}
                    {isStaff && !['AP', 'RE'].includes(req.status) && (
                      <Button variant="outline-danger" size="sm" className="rounded-pill px-3"
                        onClick={() => openRejectPanel(req)}
                        disabled={processingId === req.id}>
                        <X size={14}/> Rechazar
                      </Button>
                    )}
                    {isStaff && req.status === 'VC' && (
                      <Button variant="success" size="sm" className="rounded-pill px-3 fw-bold d-flex align-items-center gap-1 shadow-sm"
                        onClick={() => openApprovePanel(req)}
                        disabled={processingId === req.id}>
                        {processingId === req.id ? <Spinner animation="border" size="sm"/> : <Eye size={14}/>}
                        Previsualizar y Aprobar
                      </Button>
                    )}
                    {req.status === 'RE' && (
                      <div className="text-muted x-small text-end">
                        <AlertCircle size={12} className="me-1"/>
                        Rechazado: {req.rejection_reason}
                      </div>
                    )}
                    {req.status === 'AP' && (
                      <div className="text-success small fw-bold">
                        <Check size={14} className="me-1"/> Aprobado por {req.processed_by_name}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Vista mobile: tarjetas verticales */}
      <div className="d-flex d-md-none flex-column gap-3">
        {requests.map(req => (
          <div key={req.id} className="bg-white rounded-3 shadow-sm overflow-hidden">
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <div className="p-2 bg-light rounded-circle"><User size={15}/></div>
                <div>
                  <div className="fw-bold text-dark" style={{fontSize:'0.9rem'}}>{req.student_name}</div>
                  <div className="text-muted" style={{fontSize:'0.72rem'}}>{req.student_email}</div>
                </div>
              </div>
              {getStatusBadge(req.status)}
            </div>
            <div className="px-3 py-2 d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-2">
                <BookOpen size={13} className="text-primary flex-shrink-0"/>
                <span style={{fontSize:'0.85rem'}}>{req.diploma_name}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <MapPin size={13} className="text-muted flex-shrink-0"/>
                <span style={{fontSize:'0.85rem'}}>{req.municipality}</span>
              </div>
              {req.rm_comment && (
                <div className="d-flex align-items-start gap-2 text-info" style={{fontSize:'0.78rem'}}>
                  <MessageCircle size={12} className="flex-shrink-0 mt-1"/>
                  <em>Aval RM: {req.rm_comment}</em>
                </div>
              )}
              {req.processed_by_cd && (
                <div className="d-flex align-items-center gap-2 text-success" style={{fontSize:'0.78rem'}}>
                  <Check size={12} className="flex-shrink-0"/>
                  <span>VºBº CD: {req.processed_by_cd_name}{req.cd_comment ? ` — ${req.cd_comment}` : ''}</span>
                </div>
              )}
              {req.status === 'RE' && (
                <div className="d-flex align-items-start gap-2 text-danger" style={{fontSize:'0.78rem'}}>
                  <AlertCircle size={12} className="flex-shrink-0 mt-1"/>
                  <span>Rechazado: {req.rejection_reason}</span>
                </div>
              )}
              {req.status === 'AP' && (
                <div className="d-flex align-items-center gap-2 text-success" style={{fontSize:'0.78rem'}}>
                  <Check size={12} className="flex-shrink-0"/>
                  <span>Aprobado por {req.processed_by_name}</span>
                </div>
              )}
            </div>
            {!['AP', 'RE'].includes(req.status) && (isCD || isRM || isStaff) && (
              <div className="px-3 pb-3 d-flex flex-wrap gap-2 border-top pt-2">
                {isCD && !['AP', 'RE'].includes(req.status) && (
                  <Button variant="outline-secondary" size="sm" className="rounded-pill"
                    onClick={() => openCommentPanel(req, 'cd_comment')}
                    disabled={processingId === req.id}>
                    <MessageSquare size={13} className="me-1"/>Comentar
                  </Button>
                )}
                {isCD && ['PE', 'VC'].includes(req.status) && (
                  <Button variant="outline-danger" size="sm" className="rounded-pill"
                    onClick={() => openRejectPanel(req)}
                    disabled={processingId === req.id}>
                    <X size={13} className="me-1"/>Rechazar
                  </Button>
                )}
                {isCD && req.status === 'PE' && (
                  <Button variant="primary" size="sm" className="rounded-pill fw-bold"
                    onClick={() => openCommentPanel(req, 'elevate')}
                    disabled={processingId === req.id}>
                    <Send size={13} className="me-1"/>Elevar
                  </Button>
                )}
                {isRM && !['AP', 'RE'].includes(req.status) && (
                  <Button variant="outline-info" size="sm" className="rounded-pill"
                    onClick={() => openCommentPanel(req, 'rm_comment')}
                    disabled={processingId === req.id}>
                    <MessageSquare size={13} className="me-1"/>{req.rm_comment ? 'Editar Aval' : 'Dar Aval'}
                  </Button>
                )}
                {isStaff && !['AP', 'RE'].includes(req.status) && (
                  <Button variant="outline-danger" size="sm" className="rounded-pill"
                    onClick={() => openRejectPanel(req)}
                    disabled={processingId === req.id}>
                    <X size={13} className="me-1"/>Rechazar
                  </Button>
                )}
                {isStaff && req.status === 'VC' && (
                  <Button variant="success" size="sm" className="rounded-pill fw-bold"
                    onClick={() => openApprovePanel(req)}
                    disabled={processingId === req.id}>
                    {processingId === req.id ? <Spinner animation="border" size="sm"/> : <Eye size={13} className="me-1"/>}
                    Previsualizar y Aprobar
                  </Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );

  const activePanel = activeTab === 'regular' ? panel : customPanel;

  return (
    <>
    <Modal show={true} onHide={onClose} size="xl" centered backdrop="static" keyboard={false} fullscreen={isFullscreen || undefined}>
      <Modal.Header closeButton className="glassmorphism-header border-0">
        <Modal.Title className="d-flex align-items-center gap-2 flex-grow-1 flex-wrap">
          <div className="p-2 bg-primary bg-opacity-10 rounded-circle text-primary">
            <FileText size={24}/>
          </div>
          <div className="flex-grow-1">
            <h5 className="mb-0 fw-bold">Gestión de Constancias</h5>
            <small className="text-muted fw-normal">Circuito de Validación Académica y Municipal</small>
          </div>

          <div className="d-flex gap-1">
            <button
              className={`btn btn-sm rounded-pill px-3 position-relative ${activeTab === 'regular' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setActiveTab('regular')}
            >
              Alumno Regular
              {pendingRegular > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                  {pendingRegular}
                </span>
              )}
            </button>
            <button
              className={`btn btn-sm rounded-pill px-3 position-relative ${activeTab === 'custom' ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setActiveTab('custom')}
            >
              Personalizadas
              {pendingCustom > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                  {pendingCustom}
                </span>
              )}
            </button>
          </div>

          <Button
            variant="link" className="p-1 text-muted me-2"
            onClick={() => setIsFullscreen(f => !f)}
            title={isFullscreen ? 'Restaurar' : 'Maximizar'}
          >
            {isFullscreen ? <Minimize2 size={18}/> : <Maximize2 size={18}/>}
          </Button>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 bg-light bg-opacity-50" style={{ overflowY: 'auto' }}>
        {/* key forces full remount when switching between panel/tab views, avoiding portal reconciliation errors on mobile */}
        <div key={`${activeTab}-${activePanel || 'list'}`}>
          {activeTab === 'regular' ? (
            loading ? (
              <div className="text-center p-5">
                <Spinner animation="border" variant="primary"/>
                <p className="mt-2 text-muted">Cargando solicitudes...</p>
              </div>
            ) : panel === 'approve' ? (
              renderApprovePanel()
            ) : panel === 'reject' ? (
              renderRejectPanel()
            ) : panel === 'comment' ? (
              renderCommentPanel()
            ) : requests.length > 0 ? (
              renderList()
            ) : (
              <div className="text-center p-5 bg-white rounded-4 shadow-sm">
                <FileText size={48} className="text-muted opacity-25 mb-3"/>
                <h6 className="text-muted">No hay solicitudes de constancias en tu bandeja.</h6>
              </div>
            )
          ) : (
            loadingCustom ? (
              <div className="text-center p-5">
                <Spinner animation="border" variant="primary"/>
                <p className="mt-2 text-muted">Cargando solicitudes...</p>
              </div>
            ) : customPanel === 'elevate' ? (
              renderElevateCustomPanel()
            ) : customPanel === 'reject' ? (
              renderRejectCustomPanel()
            ) : customPanel === 'issue' && issueStep === 'form' ? (
              renderIssueCustomForm()
            ) : customRequests.length > 0 ? (
              renderCustomList()
            ) : (
              <div className="text-center p-5 bg-white rounded-4 shadow-sm">
                <FileText size={48} className="text-muted opacity-25 mb-3"/>
                <h6 className="text-muted">No hay solicitudes de constancias personalizadas.</h6>
              </div>
            )
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="secondary" onClick={activePanel ? (activeTab === 'regular' ? goBack : goBackCustom) : onClose}>
          {activePanel ? 'Volver' : 'Cerrar'}
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Personalizadas — paso 2: vista previa y confirmación de emisión */}
    {customPanel === 'issue' && issueStep === 'preview' && selectedCustom && (
      <CustomCertPrintModal
        title={customTitle}
        text={customText}
        studentName={selectedCustom.student_name}
        studentDni={selectedCustom.student_dni}
        diplomaName={selectedCustom.diploma_name}
        municipality={selectedCustom.municipality}
        onClose={goBackCustom}
        onBack={() => setIssueStep('form')}
        onConfirm={handleIssueCustom}
        confirmLabel="Confirmar y Emitir"
        isConfirming={!!processingCustomId}
      />
    )}

    {/* Personalizadas — ver constancia emitida */}
    {viewTarget && (
      <CustomCertPrintModal
        title={viewTarget.certificate_title}
        text={viewTarget.aco_custom_text}
        studentName={viewTarget.student_name}
        studentDni={viewTarget.student_dni}
        diplomaName={viewTarget.diploma_name}
        municipality={viewTarget.municipality}
        onClose={() => setViewTarget(null)}
      />
    )}
    </>
  );
};

export default CertificateRequestManager;
